import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import { Star, AccessTime, Person, Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';

const CoursesPage: React.FC = () => {
  const { t } = useTranslation();
  
  // 设置页面标题
  usePageTitle('courses');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');

  const courses = [
    {
      id: 1,
      titleKey: 'courses.courseList.web3Basics.title',
      descriptionKey: 'courses.courseList.web3Basics.description',
      instructorKey: 'courses.courseList.web3Basics.instructor',
      duration: '40',
      difficulty: 'beginner',
      price: 299.99,
      rating: 4.8,
      students: 1250,
      category: 'web3',
      image: '/api/placeholder/400/200'
    },
    {
      id: 2,
      titleKey: 'courses.courseList.aiIntro.title',
      descriptionKey: 'courses.courseList.aiIntro.description',
      instructorKey: 'courses.courseList.aiIntro.instructor',
      duration: '60',
      difficulty: 'beginner',
      price: 399.99,
      rating: 4.9,
      students: 2100,
      category: 'ai',
      image: '/api/placeholder/400/200'
    },
    {
      id: 3,
      titleKey: 'courses.courseList.smartContracts.title',
      descriptionKey: 'courses.courseList.smartContracts.description',
      instructorKey: 'courses.courseList.smartContracts.instructor',
      duration: '80',
      difficulty: 'intermediate',
      price: 599.99,
      rating: 4.7,
      students: 850,
      category: 'web3',
      image: '/api/placeholder/400/200'
    },
    {
      id: 4,
      titleKey: 'courses.courseList.deepLearning.title',
      descriptionKey: 'courses.courseList.deepLearning.description',
      instructorKey: 'courses.courseList.deepLearning.instructor',
      duration: '100',
      difficulty: 'advanced',
      price: 799.99,
      rating: 4.9,
      students: 650,
      category: 'ai',
      image: '/api/placeholder/400/200'
    }
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'success';
      case 'intermediate': return 'warning';
      case 'advanced': return 'error';
      default: return 'default';
    }
  };

  const categories = [
    { value: 'all', label: t('courses.filters.all') },
    { value: 'ai', label: t('courses.categories.ai') },
    { value: 'web3', label: t('courses.categories.web3') },
    { value: 'blockchain', label: t('courses.categories.blockchain') },
    { value: 'programming', label: t('courses.categories.programming') },
  ];

  const levels = [
    { value: 'all', label: t('courses.filters.all') },
    { value: 'beginner', label: t('courses.filters.beginner') },
    { value: 'intermediate', label: t('courses.filters.intermediate') },
    { value: 'advanced', label: t('courses.filters.advanced') },
  ];

  const filteredCourses = courses.filter(course => {
    const matchesSearch = t(course.titleKey).toLowerCase().includes(searchTerm.toLowerCase()) ||
                         t(course.descriptionKey).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter;
    const matchesLevel = levelFilter === 'all' || course.difficulty === levelFilter;
    
    return matchesSearch && matchesCategory && matchesLevel;
  });

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        {t('courses.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('courses.subtitle')}
      </Typography>

      {/* 搜索和筛选 */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={t('courses.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('courses.filters.category')}</InputLabel>
              <Select
                value={categoryFilter}
                label={t('courses.filters.category')}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                {categories.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('courses.filters.level')}</InputLabel>
              <Select
                value={levelFilter}
                label={t('courses.filters.level')}
                onChange={(e) => setLevelFilter(e.target.value)}
              >
                {levels.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* 课程列表 */}
      <Grid container spacing={4}>
        {filteredCourses.map((course) => (
          <Grid item xs={12} md={6} lg={4} key={course.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  height: 200,
                  backgroundColor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  {t('courses.courseImage')}
                </Typography>
              </Box>
              
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Chip
                    label={t(`courses.filters.${course.difficulty}`)}
                    color={getDifficultyColor(course.difficulty) as any}
                    size="small"
                  />
                  <Typography variant="h6" color="primary">
                    ${course.price}
                  </Typography>
                </Box>
                
                <Typography variant="h6" gutterBottom>
                  {t(course.titleKey)}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t(course.descriptionKey)}
                </Typography>
                
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {t(course.instructorKey)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {course.duration} {t('courses.courseCard.hours')}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Star sx={{ fontSize: 16, color: 'orange' }} />
                    <Typography variant="body2" color="text.secondary">
                      {course.rating} ({course.students} {t('courses.courseCard.students')})
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
              
              <CardActions sx={{ p: 2 }}>
                <Button size="small" sx={{ mr: 1 }}>
                  {t('courses.courseCard.preview')}
                </Button>
                <Button size="small" variant="contained" fullWidth>
                  {t('courses.courseCard.enroll')}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredCourses.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            {t('courses.noResults')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('courses.tryDifferentSearch')}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default CoursesPage; 